/**
 * CascadeProgressMonitor - Real-time progress tracking for cascade executions
 * Provides detailed stage-by-stage progress visualization and control
 */

import React, { useState, useEffect } from 'react';
import { 
  Pause,
  Square,
  Clock,
  DollarSign,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  BarChart3,
  TrendingUp,
  Zap
} from 'lucide-react';
import { StageType } from '@/types/execution';
import { cn } from '@/utils/cn';

interface CascadeExecution {
  id: string;
  status: 'planning' | 'queued' | 'executing' | 'completed' | 'failed' | 'cancelled' | 'paused';
  trigger_stage: StageType;
  current_stage?: StageType;
  progress: {
    percentage: number;
    executed_stages: number;
    planned_stages: number;
    current_stage_progress: number;
  };
  timing: {
    started_at?: string;
    estimated_completion?: string;
    elapsed_seconds: number;
  };
  costs: {
    estimated: number;
    actual: number;
  };
}

interface CascadeProgressMonitorProps {
  cascadeId: string;
  onPause?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  className?: string;
}

const STAGE_LABELS = {
  script: 'Script',
  vision: 'Vision', 
  storyboard: 'Storyboard',
  shots: 'Shot List',
  continuity: 'Continuity',
  frames: 'Frames',
  videos: 'Videos',
  elements: 'Elements',
  qc: 'QC',
  assemble: 'Assemble',
  music: 'Music',
  publish: 'Publish',
  music_master: 'Music Master',
  music_sidechain: 'Music Sidechain',
  export_preview: 'Export Preview'
} as const;

const CascadeProgressMonitor: React.FC<CascadeProgressMonitorProps> = ({
  cascadeId,
  onPause,
  onCancel,
  onClose,
  className
}) => {
  const [cascade, setCascade] = useState<CascadeExecution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Poll for cascade status updates
  useEffect(() => {
    const fetchCascadeStatus = async () => {
      try {
        const response = await fetch(`/api/cascade/${cascadeId}/status`, {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error('Failed to fetch cascade status');
        }
        
        const data = await response.json();
        if (data.success) {
          setCascade(data.cascade);
          setLastUpdate(new Date());
          setError(null);
        } else {
          throw new Error(data.error || 'Unknown error');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load cascade status');
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchCascadeStatus();

    // Set up polling for active cascades
    const pollInterval = setInterval(() => {
      if (cascade?.status === 'executing' || cascade?.status === 'queued') {
        fetchCascadeStatus();
      }
    }, 2000); // Poll every 2 seconds for active cascades

    return () => clearInterval(pollInterval);
  }, [cascadeId, cascade?.status]);

  const handlePause = async () => {
    try {
      const response = await fetch(`/api/cascade/${cascadeId}/pause`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to pause cascade');
      }
      
      onPause?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to pause cascade');
    }
  };

  const handleCancel = async () => {
    try {
      const response = await fetch(`/api/cascade/${cascadeId}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to cancel cascade');
      }
      
      onCancel?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel cascade');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50';
      case 'executing':
        return 'text-blue-600 bg-blue-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'paused':
        return 'text-yellow-600 bg-yellow-50';
      case 'cancelled':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'executing':
        return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4" />;
      case 'paused':
        return <Pause className="w-4 h-4" />;
      case 'cancelled':
        return <Square className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className={cn('bg-white rounded-lg border border-gray-200 p-6', className)}>
        <div className="text-center">
          <RefreshCw className="w-6 h-6 mx-auto mb-2 text-blue-600 animate-spin" />
          <p className="text-sm text-gray-600">Loading cascade status...</p>
        </div>
      </div>
    );
  }

  if (error || !cascade) {
    return (
      <div className={cn('bg-white rounded-lg border border-gray-200 p-6', className)}>
        <div className="text-center">
          <AlertCircle className="w-6 h-6 mx-auto mb-2 text-red-600" />
          <p className="text-sm text-red-800">{error || 'Cascade not found'}</p>
          {onClose && (
            <button
              onClick={onClose}
              className="mt-3 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bg-white rounded-lg border border-gray-200 shadow-lg', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className={cn('p-2 rounded-lg', getStatusColor(cascade.status))}>
            {getStatusIcon(cascade.status)}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Cascade Execution
            </h3>
            <p className="text-sm text-gray-600">
              From {STAGE_LABELS[cascade.trigger_stage]} • {cascade.status.charAt(0).toUpperCase() + cascade.status.slice(1)}
            </p>
          </div>
        </div>
        
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        )}
      </div>

      {/* Progress Overview */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">Overall Progress</span>
          <span className="text-sm text-gray-600">{cascade.progress.percentage}%</span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${cascade.progress.percentage}%` }}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="flex items-center justify-center space-x-1 mb-1">
              <BarChart3 className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-600">Stages</span>
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {cascade.progress.executed_stages}/{cascade.progress.planned_stages}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-600">Elapsed</span>
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {formatDuration(cascade.timing.elapsed_seconds)}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-center space-x-1 mb-1">
              <DollarSign className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-600">Cost</span>
            </div>
            <div className="text-lg font-semibold text-gray-900">
              ${cascade.costs.actual.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500">
              of ${cascade.costs.estimated.toFixed(2)}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-center space-x-1 mb-1">
              <TrendingUp className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-600">ETA</span>
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {cascade.timing.estimated_completion 
                ? formatTime(cascade.timing.estimated_completion)
                : '--:--'
              }
            </div>
          </div>
        </div>
      </div>

      {/* Current Activity */}
      {cascade.status === 'executing' && cascade.current_stage && (
        <div className="p-4 bg-blue-50 border-b border-blue-200">
          <div className="flex items-center space-x-2 mb-2">
            <Zap className="w-4 h-4 text-blue-600" />
            <h4 className="font-medium text-blue-900">Currently Processing</h4>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <RefreshCw className="w-4 h-4 text-white animate-spin" />
              </div>
            </div>
            
            <div className="flex-1">
              <div className="text-sm font-medium text-blue-900">
                {STAGE_LABELS[cascade.current_stage]} Stage
              </div>
              
              <div className="w-full bg-blue-200 rounded-full h-1.5 mt-1">
                <div
                  className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${cascade.progress.current_stage_progress * 100}%` }}
                />
              </div>
            </div>
            
            <div className="text-xs text-blue-700">
              {Math.round(cascade.progress.current_stage_progress * 100)}%
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      {(cascade.status === 'executing' || cascade.status === 'paused') && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="text-xs text-gray-500">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </div>
          
          <div className="flex items-center space-x-2">
            {cascade.status === 'executing' && onPause && (
              <button
                onClick={handlePause}
                className="px-3 py-1.5 text-yellow-700 bg-yellow-100 rounded-lg hover:bg-yellow-200 transition-colors flex items-center space-x-1"
              >
                <Pause className="w-3 h-3" />
                <span className="text-sm">Pause</span>
              </button>
            )}
            
            {onCancel && (
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors flex items-center space-x-1"
              >
                <Square className="w-3 h-3" />
                <span className="text-sm">Cancel</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Completion Message */}
      {cascade.status === 'completed' && (
        <div className="p-4 bg-green-50 border-b border-green-200">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <h4 className="font-medium text-green-900">Cascade Completed Successfully</h4>
              <p className="text-sm text-green-700">
                All {cascade.progress.planned_stages} stages have been regenerated with your feedback
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {cascade.status === 'failed' && (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <div>
              <h4 className="font-medium text-red-900">Cascade Failed</h4>
              <p className="text-sm text-red-700">
                The cascade execution encountered an error and could not complete
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Close Button for Completed/Failed */}
      {(cascade.status === 'completed' || cascade.status === 'failed' || cascade.status === 'cancelled') && onClose && (
        <div className="p-4 text-center">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default CascadeProgressMonitor;